import type { AnnotationCanvas } from './canvas'

const LEFT_BUTTON_DOWN = 1

export class AnnotationEventDelegator {
  private isDrawing: boolean = false
  private leftCanvas: boolean = false
  private observer: FullDrawingObserver = Object.seal({
    activate() {},
    deactivate() {},
    setup() {},
    start() {},
    move() {},
    stop() {},
    reset() {},
    undo() {},
  })
  private offset: Point

  constructor(protected canvas: AnnotationCanvas, private resizeContainer: HTMLElement) {
    this.observe(canvas)
    const { width, height, x, y } = resizeContainer.getBoundingClientRect()
    this.resizeObserver.observe(this.resizeContainer)
    this.canvas.resize(width, height)
    this.offset = { x, y }
  }

  trigger<K extends keyof DrawingObserver>(event: K, ...args: Parameters<FullDrawingObserver[K]>) {
    // @ts-ignore this seems like too much inference for TS to handle, but it's correct
    this.observer[event](...args)
  }

  activate() {
    this.canvas.addEventListener('mousedown', this.onMouseDown)
    this.canvas.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
    this.canvas.addEventListener('touchstart', this.onMouseDown)
    this.canvas.addEventListener('touchmove', this.onMouseMove)
    document.addEventListener('touchend', this.onMouseUp)
    this.canvas.addEventListener('mouseleave', this.leaveCanvas)
    this.canvas.addEventListener('mouseenter', this.reenterCanvas)
    this.trigger('activate')
  }

  deactivate() {
    if (this.isDrawing) {
      this.onMouseUp(new MouseEvent('mouseup'))
    }
    this.canvas.removeEventListener('mousemove', this.onMouseMove)
    this.canvas.removeEventListener('mousedown', this.onMouseDown)
    document.removeEventListener('mouseup', this.onMouseUp)
    this.canvas.removeEventListener('touchstart', this.onMouseDown)
    this.canvas.removeEventListener('touchmove', this.onMouseMove)
    document.removeEventListener('touchend', this.onMouseUp)
    this.canvas.removeEventListener('mouseleave', this.leaveCanvas)
    this.canvas.removeEventListener('mouseenter', this.reenterCanvas)
    this.trigger('deactivate')
  }

  observe(newObserver: DrawingObserver) {
    const keys = Object.keys(this.observer) as Array<keyof DrawingObserver>
    keys.forEach(key => {
      if (!(key in newObserver)) return

      const observer = this.observer
      const oldFn = observer[key]
      observer[key] = function () {
        ;(<Function>oldFn).apply(null, arguments)
        ;(<Function>newObserver[key]).apply(newObserver, arguments)
      }
    })
  }

  // Watch for resize of the container and resize canvas accordingly
  private resizeObserver: ResizeObserver = new ResizeObserver(
    ([
      {
        contentRect: { width, height },
        target,
      },
    ]) => {
      this.handleResize(width, height)
      const { x, y } = target.getBoundingClientRect()
      this.offset = { x, y }
    }
  )

  protected handleResize(width: number, height: number) {
    this.canvas.resize(width, height)
  }

  private leaveCanvas = (e: MouseEvent) => {
    if (e.buttons & LEFT_BUTTON_DOWN && this.isDrawing) {
      this.isDrawing = false
      this.leftCanvas = true
      this.trigger('stop', this.canvas.normalize(this.getCoords(e)))
    }
  }

  private reenterCanvas = (e: MouseEvent) => {
    if (e.buttons & LEFT_BUTTON_DOWN && this.leftCanvas) {
      this.isDrawing = true
      this.trigger('start', this.canvas.normalize(this.getCoords(e)))
    }
    this.leftCanvas = false
  }

  private onMouseDown = (e: MouseEvent | TouchEvent) => {
    if (!(e instanceof MouseEvent) || e.buttons & LEFT_BUTTON_DOWN) {
      e.preventDefault()

      this.isDrawing = true
      this.trigger('start', this.canvas.normalize(this.getCoords(e)))
    }
  }

  private onMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!this.isDrawing) return
    e.preventDefault()

    this.trigger('move', this.canvas.normalize(this.getCoords(e)))
    this.trigger('stop', this.canvas.normalize(this.getCoords(e))) // renders to screen
  }

  private onMouseUp = (_e: MouseEvent | TouchEvent) => {
    this.isDrawing = false
    this.leftCanvas = false
  }

  private getCoords(e: MouseEvent | TouchEvent): Point {
    let coords: Point
    if (e instanceof MouseEvent) {
      coords = { x: e.offsetX, y: e.offsetY }
    } else {
      coords = {
        x: e.targetTouches[0].clientX - this.offset.x,
        y: e.targetTouches[0].clientY - this.offset.y,
      }
      e.preventDefault()
    }
    return coords
  }
}
